import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { FileText, AlertTriangle, Mail, Upload, X, CheckCircle, Info } from 'lucide-react';
import Modal from './Modal';
import { LeadDto } from '../services/leadsService';
import {
  contractApi,
  InitiateSigningDto,
  ContractDto,
  ManualUploadSigningDto,
  ManualUploadFiles,
  validateAccessCode,
  validateCommissionPercentage,
  validateEmail,
  validateIsraeliId,
  generateRandomAccessCode,
} from '../services/contractService';
import {
  leadSigningApi,
  LeadSigningDetailsDto,
  emptyParent,
  emptySpouse,
  emptySubmitter,
} from '../services/leadSigningService';
import { validateIdField } from '../utils/validationUtils';
import { clientsApi } from '../services/clientsService';
import LeadSigningDetails from './LeadSigningDetails';
import AddressConfirmationCheckbox from './AddressConfirmationCheckbox';
import { ADDRESS_CONFIRMATION_CHECKBOX_LABEL, ADDRESS_CONFIRMATION_REQUIRED_ERROR } from '../constants/addressConfirmation';

type SigningMethod = 'digital' | 'manual';

interface InitiateSigningModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (contract: ContractDto) => void;
  lead: LeadDto;
}

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-bottom: 20px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
`;

const RequiredLabel = styled(Label)`
  &::after {
    content: ' *';
    color: var(--error-color);
  }
`;

const Input = styled.input<{ hasError?: boolean }>`
  padding: 10px 12px;
  border: 1px solid ${props => props.hasError ? 'var(--error-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? 'var(--error-color)' : 'var(--primary-color)'};
    box-shadow: 0 0 0 2px ${props => props.hasError ? 'var(--error-light)' : 'var(--primary-light)'};
  }
`;

const ErrorText = styled.span`
  font-size: 12px;
  color: var(--error-color);
  margin-top: 4px;
`;

const SuccessText = styled.span`
  font-size: 12px;
  color: var(--success-color);
  margin-top: 4px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--text-secondary);
  cursor: pointer;
  margin-top: 6px;

  input {
    width: 16px;
    height: 16px;
    cursor: pointer;
    accent-color: var(--primary-color);
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'outline' }>`
  padding: 10px 16px;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: var(--primary-color);
          color: white;
          border: 1px solid var(--primary-color);
          &:hover:not(:disabled) {
            background: transparent;
            color: var(--primary-color);
          }
        `;
      case 'outline':
        return `
          background: transparent;
          color: var(--primary-color);
          border: 1px solid var(--primary-color);
          &:hover:not(:disabled) {
            background: var(--primary-light);
            border-color: var(--primary-hover);
          }
        `;
      default:
        return `
          background: var(--surface-color);
          color: var(--text-primary);
          border: 1px solid var(--border-color);
          &:hover:not(:disabled) { background: var(--hover-background); }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;

  @media (max-width: 767px) {
    flex-direction: column-reverse;

    & > * {
      width: 100%;
    }
  }
`;

const AccessCodeGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-end;
`;

const AccessCodeInput = styled(Input)`
  flex: 1;
`;

const GenerateButton = styled(Button)`
  flex-shrink: 0;
  padding: 10px 12px;
  font-size: 12px;
`;

const InfoBox = styled.div`
  background: var(--primary-light);
  color: var(--primary-color);
  padding: 12px 16px;
  border-radius: var(--border-radius-md);
  margin-bottom: 16px;
  border: 1px solid var(--primary-color);
  font-size: 14px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const InfoIcon = styled.span`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const LeadInfo = styled.div`
  background: var(--background-color);
  padding: 12px 16px;
  border-radius: var(--border-radius-sm);
  margin-bottom: 16px;
  border-right: 3px solid var(--primary-color);
`;

const LeadInfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
  font-size: 14px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const WarningBox = styled.div`
  background: #fef3c7;
  color: #92400e;
  padding: 12px 16px;
  border-radius: var(--border-radius-md);
  margin-bottom: 16px;
  border: 1px solid #f59e0b;
  font-size: 14px;

  ul {
    margin: 8px 0 0 0;
    padding-right: 20px;
  }

  li {
    margin-bottom: 4px;
  }
`;

const WarningHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const WarningIcon = styled.span`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const MethodSelector = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 20px;
`;

const MethodButton = styled.button<{ $active?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px 12px;
  border: 2px solid ${props => props.$active ? 'var(--primary-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-md);
  background: ${props => props.$active ? 'var(--primary-light)' : 'white'};
  color: ${props => props.$active ? 'var(--primary-color)' : 'var(--text-primary)'};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: 500;

  &:hover {
    border-color: var(--primary-color);
    background: var(--primary-light);
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const FileUploadSection = styled.div`
  margin-bottom: 20px;
`;

const FileUploadBox = styled.div<{ hasError?: boolean; hasFile?: boolean }>`
  border: 2px dashed ${props => props.hasError ? 'var(--error-color)' : props.hasFile ? 'var(--success-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-md);
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.hasFile ? 'var(--success-light, #f0fdf4)' : 'var(--background-color)'};
  margin-bottom: 12px;

  &:hover {
    border-color: ${props => props.hasError ? 'var(--error-color)' : 'var(--primary-color)'};
    background: var(--primary-light);
  }
`;

const FileUploadLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 4px;
`;

const FileUploadHint = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
`;

const FileUploadIcon = styled.div<{ hasFile?: boolean }>`
  color: ${props => props.hasFile ? 'var(--success-color)' : 'var(--text-secondary)'};
  margin-bottom: 8px;
`;

const SelectedFileName = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 13px;
  color: var(--success-color);
  margin-top: 8px;
`;

const RemoveFileButton = styled.button`
  background: none;
  border: none;
  color: var(--error-color);
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;

  &:hover {
    opacity: 0.8;
  }
`;

const SectionHeader = styled.div`
  background: var(--primary-light);
  padding: 12px 16px;
  border-radius: var(--border-radius-sm);
  margin: 20px 0 12px 0;
  border-right: 3px solid var(--primary-color);
  font-weight: 600;
  color: var(--primary-color);
`;

const LoadingBlock = styled.div`
  padding: 24px;
  text-align: center;
  color: var(--text-secondary);
  font-size: 14px;
`;

interface ModalFormState {
  email: string;
  commissionPercentage: number;
  accessCode: string;
}

const InitiateSigningModal: React.FC<InitiateSigningModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  lead,
}) => {
  // אחוז עמלת ברירת מחדל
  const defaultCommission = 20;
  const [signingMethod, setSigningMethod] = useState<SigningMethod>('digital');

  const [uploadedFiles, setUploadedFiles] = useState<{
    nationalInsuranceAuth: File | null;
    serviceAgreement: File | null;
  }>({ nationalInsuranceAuth: null, serviceAgreement: null });

  const nationalInsuranceAuthInputRef = useRef<HTMLInputElement>(null);
  const serviceAgreementInputRef = useRef<HTMLInputElement>(null);

  const [modalForm, setModalForm] = useState<ModalFormState>({
    email: lead.email || '',
    commissionPercentage: defaultCommission,
    accessCode: '',
  });

  const [signingDetails, setSigningDetails] = useState<LeadSigningDetailsDto | null>(null);
  const [signingDetailsLoading, setSigningDetailsLoading] = useState(false);

  // אישור הנציג שהכתובת תואמת לכתובת במשרד הפנים — תנאי חובה לחתימה דיגיטלית
  const [addressConfirmed, setAddressConfirmed] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [idNumberDuplicateError, setIdNumberDuplicateError] = useState<string | null>(null);
  const [checkingIdNumber, setCheckingIdNumber] = useState(false);

  // טעינה ראשונית של פרטי החתימה מהשרת בכל פתיחה
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setSigningDetailsLoading(true);
    leadSigningApi.get(lead.id)
      .then(data => { if (!cancelled) setSigningDetails(data); })
      .catch(err => {
        console.error('Failed to load signing details:', err);
        if (!cancelled) {
          setErrors(prev => ({ ...prev, general: 'אירעה שגיאה בטעינת פרטי החתימה' }));
          setSigningDetails({
            id: lead.id,
            benefitType: lead.benefitType,
            beneficiaryType: 'adult',
            isClientForeignResident: false,
            father: emptyParent(),
            mother: emptyParent(),
            spouse: emptySpouse(),
            submitter: emptySubmitter(),
          });
        }
      })
      .finally(() => { if (!cancelled) setSigningDetailsLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, lead.id, lead.benefitType]);

  // ============================================================
  // ולידציה
  // ============================================================
  const validateForm = (): Record<string, string> => {
    const newErrors: Record<string, string> = {};

    if (signingMethod === 'digital') {
      if (!modalForm.email.trim()) {
        newErrors.email = 'דוא"ל הוא שדה חובה';
      } else if (!validateEmail(modalForm.email)) {
        newErrors.email = 'כתובת דוא"ל לא תקינה';
      }

      if (!modalForm.accessCode.trim()) {
        newErrors.accessCode = 'קוד גישה הוא שדה חובה';
      } else if (!validateAccessCode(modalForm.accessCode)) {
        newErrors.accessCode = 'קוד גישה חייב להכיל בין 4 ל-20 תווים';
      }
    }

    if (!validateCommissionPercentage(modalForm.commissionPercentage)) {
      newErrors.commissionPercentage = 'אחוז עמלה חייב להיות בין 1 ל-100';
    }

    if (signingMethod === 'manual') {
      if (!uploadedFiles.nationalInsuranceAuth) {
        newErrors.nationalInsuranceAuth = 'נדרש להעלות קובץ ייפוי כוח';
      }
      if (!uploadedFiles.serviceAgreement) {
        newErrors.serviceAgreement = 'נדרש להעלות קובץ הסכם שירות';
      }
      // ת.ז. הלקוח — חובה גם בהעלאה ידנית (נשמרת על הלקוח שנוצר)
      if (signingDetails) {
        if (!signingDetails.clientIdNumber?.trim()) {
          newErrors.clientIdNumber = 'ת.ז. הלקוח היא שדה חובה';
        } else {
          const idError = validateIdField(signingDetails.clientIdNumber, signingDetails.isClientForeignResident);
          if (idError) newErrors.clientIdNumber = idError;
        }
      }
    }

    // אישור התאמת כתובת — תנאי חובה לחתימה דיגיטלית
    if (signingMethod === 'digital' && !addressConfirmed) {
      newErrors.addressConfirmed = ADDRESS_CONFIRMATION_REQUIRED_ERROR;
    }

    // ולידציה של בלוק פרטי החתימה — רק בחתימה דיגיטלית
    if (signingMethod === 'digital' && signingDetails) {
      if (!signingDetails.clientAddress?.trim()) newErrors.clientAddress = 'כתובת היא שדה חובה';
      if (!signingDetails.clientCity?.trim()) newErrors.clientCity = 'עיר היא שדה חובה';
      if (!signingDetails.clientIdNumber?.trim()) {
        newErrors.clientIdNumber = 'ת.ז. הלקוח היא שדה חובה';
      } else {
        const idError = validateIdField(signingDetails.clientIdNumber, signingDetails.isClientForeignResident);
        if (idError) newErrors.clientIdNumber = idError;
      }

      if (signingDetails.beneficiaryType === 'child') {
        const requireParent = (which: 'father' | 'mother') => {
          const parent = signingDetails[which];
          if (!parent.name?.trim()) newErrors[which + '.name'] = 'שם הוא שדה חובה';
          if (!parent.idNumber?.trim()) {
            newErrors[which + '.idNumber'] = 'ת.ז. היא שדה חובה';
          } else {
            const idError = validateIdField(parent.idNumber, parent.isForeignResident);
            if (idError) newErrors[which + '.idNumber'] = idError;
          }
          if (!parent.address?.trim()) newErrors[which + '.address'] = 'כתובת היא שדה חובה';
          if (!parent.city?.trim()) newErrors[which + '.city'] = 'עיר היא שדה חובה';
        };
        requireParent('father');
        requireParent('mother');
      }

      // מגיש הבקשה — שם, ת.ז. וקרבה חובה
      const submitter = signingDetails.submitter;
      if (!submitter.name?.trim()) newErrors['submitter.name'] = 'שם המגיש הוא שדה חובה';
      if (!submitter.idNumber?.trim()) {
        newErrors['submitter.idNumber'] = 'ת.ז. המגיש היא שדה חובה';
      } else if (submitter.sourceType === 'other') {
        const idError = validateIdField(submitter.idNumber, false);
        if (idError) newErrors['submitter.idNumber'] = idError;
      }
      if (!submitter.relationship?.trim()) newErrors['submitter.relationship'] = 'קרבת משפחה היא שדה חובה';
    }

    if (idNumberDuplicateError) {
      newErrors.clientIdNumber = idNumberDuplicateError;
    }

    setErrors(newErrors);
    return newErrors;
  };

  // גלילה לשדה הראשון השגוי לפי סדר ה-DOM
  const scrollToFirstError = (errorMap: Record<string, string>) => {
    const candidates = document.querySelectorAll<HTMLElement>('[data-error-key]');
    for (const el of Array.from(candidates)) {
      const key = el.dataset.errorKey;
      if (key && errorMap[key]) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = el.querySelector<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('input, select, textarea');
        input?.focus({ preventScroll: true });
        return;
      }
    }
  };

  const checkIdNumberDuplicate = async (idNumber: string) => {
    if (!idNumber || !validateIsraeliId(idNumber)) {
      setIdNumberDuplicateError(null);
      return;
    }
    setCheckingIdNumber(true);
    try {
      const result = await clientsApi.checkIdNumberExists(idNumber);
      if (result.exists) {
        setIdNumberDuplicateError('תעודת זהות זו כבר קיימת במערכת. לא ניתן לשלוח חוזה לחתימה.');
      } else {
        setIdNumberDuplicateError(null);
      }
    } catch (error) {
      console.error('Error checking ID number:', error);
      setIdNumberDuplicateError(null);
    } finally {
      setCheckingIdNumber(false);
    }
  };

  // בדיקת כפילות ת.ז. כאשר המשתמש משנה אותה
  useEffect(() => {
    if (!signingDetails?.clientIdNumber) {
      setIdNumberDuplicateError(null);
      return;
    }
    const handler = setTimeout(() => {
      checkIdNumberDuplicate(signingDetails.clientIdNumber || '');
    }, 500);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signingDetails?.clientIdNumber]);

  const handleGenerateAccessCode = () => {
    setModalForm(prev => ({ ...prev, accessCode: generateRandomAccessCode() }));
    if (errors.accessCode) setErrors(prev => ({ ...prev, accessCode: '' }));
  };

  const handleFileSelect = (
    type: 'nationalInsuranceAuth' | 'serviceAgreement',
    file: File | null,
  ) => {
    if (file) {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        setErrors(prev => ({ ...prev, [type]: 'יש להעלות קובץ PDF בלבד' }));
        return;
      }
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        setErrors(prev => ({ ...prev, [type]: 'גודל הקובץ לא יכול לעלות על 10MB' }));
        return;
      }
      setErrors(prev => ({ ...prev, [type]: '' }));
    }
    setUploadedFiles(prev => ({ ...prev, [type]: file }));
  };

  const handleFileInputChange = (
    type: 'nationalInsuranceAuth' | 'serviceAgreement',
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    handleFileSelect(type, event.target.files?.[0] || null);
  };

  const handleRemoveFile = (type: 'nationalInsuranceAuth' | 'serviceAgreement') => {
    setUploadedFiles(prev => ({ ...prev, [type]: null }));
    if (type === 'nationalInsuranceAuth' && nationalInsuranceAuthInputRef.current) {
      nationalInsuranceAuthInputRef.current.value = '';
    } else if (type === 'serviceAgreement' && serviceAgreementInputRef.current) {
      serviceAgreementInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      scrollToFirstError(validationErrors);
      return;
    }

    try {
      setLoading(true);

      // שלב 1: שמירת פרטי החתימה (גם בדיגיטלי וגם בידני, אם נטענו)
      // — חשוב גם בידני כי CreateClientFromContractAsync מעתיק שדות אלו ללקוח
      if (signingDetails) {
        await leadSigningApi.update(lead.id, signingDetails);
      }

      // שלב 2: יצירת חוזה
      if (signingMethod === 'digital') {
        const dto: InitiateSigningDto = {
          leadId: lead.id,
          email: modalForm.email,
          commissionPercentage: modalForm.commissionPercentage,
          accessCode: modalForm.accessCode,
          addressConfirmed,
        };
        const contract = await contractApi.initiateSigning(dto);
        onSuccess(contract);
        onClose();
      } else {
        if (!uploadedFiles.nationalInsuranceAuth || !uploadedFiles.serviceAgreement) {
          setErrors({ general: 'יש להעלות את שני המסמכים' });
          return;
        }

        // בהעלאה ידנית — ת.ז. הלקוח נדרשת
        const clientIdNumber = signingDetails?.clientIdNumber?.trim() || lead.clientIdNumber || '';
        if (!clientIdNumber) {
          setErrors({ general: 'תעודת זהות הלקוח חייבת להיות מוגדרת לפני העלאת מסמכים' });
          return;
        }

        const manualData: ManualUploadSigningDto = {
          leadId: lead.id,
          commissionPercentage: modalForm.commissionPercentage,
          clientIdNumber,
        };
        const files: ManualUploadFiles = {
          nationalInsuranceAuth: uploadedFiles.nationalInsuranceAuth,
          serviceAgreement: uploadedFiles.serviceAgreement,
        };
        const contract = await contractApi.manualUploadSigning(manualData, files);
        onSuccess(contract);
        onClose();
      }
    } catch (error: any) {
      console.error('Error processing signing:', error);
      if (error.response?.data?.message) {
        if (error.response.status === 409 && error.response.data.message.includes('תעודת זהות')) {
          setIdNumberDuplicateError(error.response.data.message);
        } else if (error.response.status === 409) {
          setErrors({ general: 'כבר קיים חוזה עבור ליד זה' });
        } else {
          setErrors({ general: error.response.data.message });
        }
      } else {
        setErrors({
          general: signingMethod === 'digital'
            ? 'אירעה שגיאה בשליחת המסמכים לחתימה'
            : 'אירעה שגיאה בהעלאת המסמכים החתומים',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setSigningMethod('digital');
    setUploadedFiles({ nationalInsuranceAuth: null, serviceAgreement: null });
    setModalForm({ email: lead.email || '', commissionPercentage: defaultCommission, accessCode: '' });
    setSigningDetails(null);
    setAddressConfirmed(false);
    setErrors({});
    setIdNumberDuplicateError(null);
    onClose();
  };

  // רשימת שדות חתימה חסרים — לתצוגה של אזהרה
  const getMissingFields = (): string[] => {
    if (signingMethod === 'manual' || !signingDetails) return [];
    const missing: string[] = [];
    if (!signingDetails.clientAddress?.trim()) missing.push('כתובת');
    if (!signingDetails.clientCity?.trim()) missing.push('עיר');
    if (!signingDetails.clientIdNumber?.trim()) missing.push('תעודת זהות הלקוח');
    if (signingDetails.beneficiaryType === 'child') {
      if (!signingDetails.father.name?.trim()) missing.push('שם האב');
      if (!signingDetails.father.idNumber?.trim()) missing.push('ת.ז. אב');
      if (!signingDetails.mother.name?.trim()) missing.push('שם האם');
      if (!signingDetails.mother.idNumber?.trim()) missing.push('ת.ז. אם');
    }
    if (!signingDetails.submitter.name?.trim()) missing.push('שם המגיש');
    if (!signingDetails.submitter.idNumber?.trim()) missing.push('ת.ז. מגיש');
    if (!signingDetails.submitter.relationship?.trim()) missing.push('קרבת משפחה');
    if (!addressConfirmed) missing.push(ADDRESS_CONFIRMATION_CHECKBOX_LABEL);
    return missing;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={signingMethod === 'digital' ? 'שליחה לחתימה דיגיטלית' : 'העלאת חוזה חתום'}
      width="640px"
      maxWidth="95vw"
      footer={
        <ButtonGroup>
          <Button onClick={handleClose} disabled={loading}>
            ביטול
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={loading || signingDetailsLoading}>
            {loading
              ? (signingMethod === 'digital' ? 'שולח...' : 'מעלה...')
              : (signingMethod === 'digital' ? 'שלח לחתימה' : 'העלה ושמור')}
          </Button>
        </ButtonGroup>
      }
    >
      <MethodSelector>
        <MethodButton
          type="button"
          $active={signingMethod === 'digital'}
          onClick={() => setSigningMethod('digital')}
          disabled={loading}
        >
          <Mail size={24} />
          <span>שליחה לחתימה דיגיטלית</span>
        </MethodButton>
        <MethodButton
          type="button"
          $active={signingMethod === 'manual'}
          onClick={() => setSigningMethod('manual')}
          disabled={loading}
        >
          <Upload size={24} />
          <span>העלאת חוזה חתום</span>
        </MethodButton>
      </MethodSelector>

      <LeadInfo>
        <LeadInfoItem>
          <strong>שם הלקוח:</strong>
          <span>{lead.fullName}</span>
        </LeadInfoItem>
        <LeadInfoItem>
          <strong>טלפון:</strong>
          <span>{lead.contacts?.find(c => c.isPrimary)?.number ?? lead.contacts?.[0]?.number ?? ''}</span>
        </LeadInfoItem>
        <LeadInfoItem>
          <strong>סטטוס:</strong>
          <span style={{ color: lead.statusColor }}>{lead.statusName}</span>
        </LeadInfoItem>
      </LeadInfo>

      <InfoBox>
        <InfoIcon>
          {signingMethod === 'digital' ? <Mail size={18} strokeWidth={2} /> : <Upload size={18} strokeWidth={2} />}
        </InfoIcon>
        <span>
          {signingMethod === 'digital'
            ? 'המערכת תשלח אימייל ללקוח עם קישור לחתימה דיגיטלית וקוד הגישה שהגדרת'
            : 'העלה את המסמכים החתומים (PDF בלבד). הלקוח יווצר אוטומטית לאחר ההעלאה.'}
        </span>
      </InfoBox>

      {signingMethod === 'manual' && (
        <FileUploadSection>
          <SectionHeader>העלאת מסמכים חתומים</SectionHeader>

          <input
            type="file"
            ref={nationalInsuranceAuthInputRef}
            accept=".pdf"
            style={{ display: 'none' }}
            onChange={(e) => handleFileInputChange('nationalInsuranceAuth', e)}
          />
          <FileUploadBox
            data-error-key="nationalInsuranceAuth"
            hasError={!!errors.nationalInsuranceAuth}
            hasFile={!!uploadedFiles.nationalInsuranceAuth}
            onClick={() => nationalInsuranceAuthInputRef.current?.click()}
          >
            <FileUploadIcon hasFile={!!uploadedFiles.nationalInsuranceAuth}>
              {uploadedFiles.nationalInsuranceAuth ? <CheckCircle size={32} /> : <FileText size={32} />}
            </FileUploadIcon>
            <FileUploadLabel>ייפוי כוח ביטוח לאומי (PDF) *</FileUploadLabel>
            <FileUploadHint>לחץ כאן לבחירת קובץ או גרור לכאן</FileUploadHint>
            {uploadedFiles.nationalInsuranceAuth && (
              <SelectedFileName>
                <span>{uploadedFiles.nationalInsuranceAuth.name}</span>
                <RemoveFileButton
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveFile('nationalInsuranceAuth');
                  }}
                >
                  <X size={16} />
                </RemoveFileButton>
              </SelectedFileName>
            )}
          </FileUploadBox>
          {errors.nationalInsuranceAuth && <ErrorText>{errors.nationalInsuranceAuth}</ErrorText>}

          <input
            type="file"
            ref={serviceAgreementInputRef}
            accept=".pdf"
            style={{ display: 'none' }}
            onChange={(e) => handleFileInputChange('serviceAgreement', e)}
          />
          <FileUploadBox
            data-error-key="serviceAgreement"
            hasError={!!errors.serviceAgreement}
            hasFile={!!uploadedFiles.serviceAgreement}
            onClick={() => serviceAgreementInputRef.current?.click()}
          >
            <FileUploadIcon hasFile={!!uploadedFiles.serviceAgreement}>
              {uploadedFiles.serviceAgreement ? <CheckCircle size={32} /> : <FileText size={32} />}
            </FileUploadIcon>
            <FileUploadLabel>הסכם שירות (PDF) *</FileUploadLabel>
            <FileUploadHint>לחץ כאן לבחירת קובץ או גרור לכאן</FileUploadHint>
            {uploadedFiles.serviceAgreement && (
              <SelectedFileName>
                <span>{uploadedFiles.serviceAgreement.name}</span>
                <RemoveFileButton
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveFile('serviceAgreement');
                  }}
                >
                  <X size={16} />
                </RemoveFileButton>
              </SelectedFileName>
            )}
          </FileUploadBox>
          {errors.serviceAgreement && <ErrorText>{errors.serviceAgreement}</ErrorText>}
        </FileUploadSection>
      )}

      {errors.general && (
        <ErrorText style={{ display: 'block', marginBottom: '16px', textAlign: 'center' }}>
          {errors.general}
        </ErrorText>
      )}

      {/* פרטי חתימה דרך הקומפוננטה החדשה — רק בחתימה דיגיטלית */}
      {signingMethod === 'digital' && (
        <>
          {signingDetailsLoading && <LoadingBlock>טוען פרטי חתימה...</LoadingBlock>}
          {!signingDetailsLoading && signingDetails && (
            <>
              {(() => {
                const missing = getMissingFields();
                return missing.length > 0 && (
                  <WarningBox>
                    <WarningHeader>
                      <WarningIcon><AlertTriangle size={18} strokeWidth={2} /></WarningIcon>
                      <span>נדרשים פרטים נוספים לפני שליחת החוזה:</span>
                    </WarningHeader>
                    <ul>
                      {missing.map(f => <li key={f}>{f}</li>)}
                    </ul>
                  </WarningBox>
                );
              })()}

              <LeadSigningDetails
                value={signingDetails}
                errors={errors}
                disabled={loading}
                onChange={setSigningDetails}
                onErrorsChange={setErrors}
              />

              {/* אישור התאמת הכתובת לכתובת המעודכנת במשרד הפנים — חובה לחתימה דיגיטלית */}
              <div data-error-key="addressConfirmed">
                <AddressConfirmationCheckbox
                  checked={addressConfirmed}
                  onChange={(checked) => {
                    setAddressConfirmed(checked);
                    if (checked && errors.addressConfirmed) {
                      setErrors(prev => {
                        const next = { ...prev };
                        delete next.addressConfirmed;
                        return next;
                      });
                    }
                  }}
                  disabled={loading}
                  error={errors.addressConfirmed}
                />
              </div>
            </>
          )}
        </>
      )}

      {/* פרטי החתימה הכלליים — אימייל / עמלה / קוד גישה */}
      <SectionHeader>{signingMethod === 'digital' ? 'פרטי שליחה' : 'פרטי החוזה'}</SectionHeader>
      <FormGrid>
        {/* ת.ז. הלקוח — נדרשת בהעלאה ידנית (בדיגיטלי היא נאספת דרך בלוק פרטי החתימה) */}
        {signingMethod === 'manual' && signingDetails && (
          <FormField data-error-key="clientIdNumber">
            <RequiredLabel>תעודת זהות הלקוח</RequiredLabel>
            <Input
              type="text"
              inputMode="numeric"
              maxLength={9}
              value={signingDetails.clientIdNumber || ''}
              onChange={(e) => {
                const digitsOnly = e.target.value.replace(/\D/g, '');
                setSigningDetails(prev => (prev ? { ...prev, clientIdNumber: digitsOnly } : prev));
                if (errors.clientIdNumber) setErrors(prev => ({ ...prev, clientIdNumber: '' }));
                if (idNumberDuplicateError) setIdNumberDuplicateError(null);
              }}
              hasError={!!errors.clientIdNumber}
              placeholder="123456789"
              disabled={loading}
            />
            {errors.clientIdNumber && <ErrorText>{errors.clientIdNumber}</ErrorText>}
            {signingDetails.clientIdNumber && !errors.clientIdNumber &&
              (signingDetails.isClientForeignResident || validateIsraeliId(signingDetails.clientIdNumber)) && (
              <SuccessText>✓ {signingDetails.isClientForeignResident ? 'תושב חו"ל' : 'ת.ז. תקין'}</SuccessText>
            )}
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={signingDetails.isClientForeignResident}
                onChange={(e) => {
                  const foreign = e.target.checked;
                  setSigningDetails(prev => (prev ? { ...prev, isClientForeignResident: foreign } : prev));
                  setErrors(prev => ({ ...prev, clientIdNumber: validateIdField(signingDetails.clientIdNumber, foreign) || '' }));
                }}
                disabled={loading}
              />
              תושב חו"ל (ללא ת.ז. ישראלית)
            </CheckboxLabel>
          </FormField>
        )}

        {signingMethod === 'digital' && (
          <FormField data-error-key="email">
            <RequiredLabel>כתובת דוא"ל</RequiredLabel>
            <Input
              type="email"
              value={modalForm.email}
              onChange={(e) => { setModalForm(prev => ({ ...prev, email: e.target.value })); if (errors.email) setErrors(prev => ({ ...prev, email: '' })); }}
              hasError={!!errors.email}
              placeholder="user@example.com"
              disabled={loading}
            />
            {errors.email && <ErrorText>{errors.email}</ErrorText>}
            {modalForm.email && validateEmail(modalForm.email) && (
              <SuccessText>✓ כתובת דוא"ל תקינה</SuccessText>
            )}
          </FormField>
        )}

        <FormField data-error-key="commissionPercentage">
          <RequiredLabel>אחוז עמלה (%)</RequiredLabel>
          <Input
            type="number"
            min="1"
            max="100"
            step="0.1"
            value={modalForm.commissionPercentage}
            onChange={(e) => { setModalForm(prev => ({ ...prev, commissionPercentage: parseFloat(e.target.value) || 0 })); if (errors.commissionPercentage) setErrors(prev => ({ ...prev, commissionPercentage: '' })); }}
            hasError={!!errors.commissionPercentage}
            placeholder="20"
            disabled={loading}
          />
          {errors.commissionPercentage && <ErrorText>{errors.commissionPercentage}</ErrorText>}
          {validateCommissionPercentage(modalForm.commissionPercentage) && (
            <SuccessText>✓ אחוז עמלה תקין</SuccessText>
          )}
        </FormField>

        {signingMethod === 'digital' && (
          <FormField data-error-key="accessCode">
            <RequiredLabel>קוד גישה לחתימה</RequiredLabel>
            <AccessCodeGroup>
              <AccessCodeInput
                type="text"
                value={modalForm.accessCode}
                onChange={(e) => { setModalForm(prev => ({ ...prev, accessCode: e.target.value.toUpperCase() })); if (errors.accessCode) setErrors(prev => ({ ...prev, accessCode: '' })); }}
                hasError={!!errors.accessCode}
                placeholder="הקלד קוד גישה או צור אוטומטית"
                disabled={loading}
              />
              <GenerateButton
                variant="outline"
                onClick={handleGenerateAccessCode}
                disabled={loading}
                type="button"
              >
                צור קוד
              </GenerateButton>
            </AccessCodeGroup>
            {errors.accessCode && <ErrorText>{errors.accessCode}</ErrorText>}
            {modalForm.accessCode && validateAccessCode(modalForm.accessCode) && (
              <SuccessText>✓ קוד גישה תקין (זכור לשמור אותו!)</SuccessText>
            )}
          </FormField>
        )}

        {idNumberDuplicateError && (
          <ErrorText>{idNumberDuplicateError}</ErrorText>
        )}
        {checkingIdNumber && (
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>בודק ת.ז....</span>
        )}
      </FormGrid>

      <InfoBox>
        <InfoIcon><Info size={16} strokeWidth={2} /></InfoIcon>
        <span>המזכירות המשויכות לליד זה יועתקו אוטומטית ללקוח החדש שייווצר</span>
      </InfoBox>
    </Modal>
  );
};

export default InitiateSigningModal;
